import { cacheDecorator } from '@ds/swymkit/tools'

import { commentFetch } from '~/services/fetch-comment'
import { swymFetch } from '~/services/fetch-swym'

import { _userParser } from '~/entities/user/user/api/parser-users'
import type { IUser } from '~/entities/user/user/interfaces/user'

import { commentParser, commentsParser, commentsResponseParser } from '~/features-swym/comment/api/parser-comments'
import { type IAnswer, type IComment, ModerationStatus } from '~/features-swym/comment/interfaces/comment'
import type { CommentResponse, ICommentsResponse, UserEndorseResponse } from '~/features-swym/comment/interfaces/swym-comments'

import { getPlatformId } from '~/features-shared/config-manager/stores/swym-config'
import type { SwymAuthor } from '~/features-shared/content/interfaces/swym-content'

import type { WithRequired } from '~/features-generic/libs/interfaces/utils'
import { mutualizerDecorator } from '~/features-generic/services/utils/decorator-mutualizer'

import type { SwymResponse } from '~/interfaces/services-dto'

interface _GetCommentsParams {
  comment_uri?: string
  endorse_uri?: string
  page?: number
  limit?: number
  sort?: 'time'
  order?: 'reverse_time'
  tenant?: string
  offset?: number
}
interface GetCommentsParams extends _GetCommentsParams {
  subject_uri: string | string[]
}
interface GetCommentsParamsMutualized extends _GetCommentsParams {
  subject_uri: string
}

interface CommentsRightData {
  hasModerationRight: boolean
}

async function _getComments(options: GetCommentsParams):
Promise<SwymResponse<Record<string, ICommentsResponse>> & CommentsRightData> {
  const platformId = getPlatformId()

  // eslint-disable-next-line max-len
  function isResultACommentResponse(result: ICommentsResponse | Record<string, ICommentsResponse>, subjectUri: string | string[]): result is ICommentsResponse {
    if (typeof subjectUri === 'string')
      return !Object.hasOwn(result, subjectUri)
    else if (subjectUri.length === 1)
      return !Object.hasOwn(result, subjectUri[0])

    return false
  }

  return swymFetch
    .get(`/commentproxy/subjects/${options.subject_uri}/contributions`, {
      searchParams: {
        tenant: options.tenant || platformId,
        page_number: options.page || 0,
        page_size: Number.isInteger(options.limit) ? (options.limit as number) : 8,
        sort_by: options.sort || 'time',
        order_by: options.order || 'reverse_time',
      },
    })
    .then(resp => resp
      .json<SwymResponse<ICommentsResponse | Record<string, ICommentsResponse>>>()
      .then((response) => {
        const hasModerationRight = resp.headers.get('X-3dcomment-Has-Moderation-Rights') === 'true'
        const result = response.result

        if (isResultACommentResponse(result, options.subject_uri)) {
          let subjectUri: string
          if (typeof options.subject_uri === 'string')
            subjectUri = options.subject_uri
          else
            subjectUri = options.subject_uri[0]

          return {
            hasModerationRight,
            ...response,
            result: {
              [subjectUri]: result,
            },
          }
        }

        return {
          hasModerationRight,
          ...response,
          result,
        }
      }))
}

/**
 * Get comments from the provided offset.
 * @param options - Options.
 * @param options.subject_uri - Subject URI.
 * @param options.offset - Offset number.
 * @param options.limit - Max number of comments to fetch.
 * @returns Promise with the fetched comments.
 */
export async function getCommentsFromOffset({
  subject_uri,
  offset,
  limit = 10,
}: WithRequired<GetCommentsParams, 'subject_uri' | 'offset'>): Promise<IComment[]> {
  return swymFetch
    .get(`/commentproxy/subjects/${subject_uri}/comments`, {
      searchParams: {
        page_size: limit,
        offset,
      },
    })
    .json<SwymResponse<CommentResponse[]>>()
    .then((response) => {
      return commentsParser(response.result)
    })
}

const _getCommentsMutualized = mutualizerDecorator<
typeof _getComments,
  GetCommentsParamsMutualized,
  ReturnType<typeof commentsResponseParser>
>({
  fetch: _getComments,
  merge: (bucketList) => {
    const uris = bucketList.map(el => el.subject_uri)

    // we will always use the biggest limit, if we only use the bucketList.pop(), limit will be uncertain as it will be the limit of the last entry of the bucket list
    // in the case not all the entries have a limit, we skip those who don't have one
    // in the edge case where none entries have a limit, we set the limit to 0
    const limit = bucketList.reduce((p, n) => {
      if (p.limit && n.limit)
        return p.limit > n.limit ? p : n
      else
        return p?.limit ? p : n
    })?.limit || 0
    return {
      ...bucketList.pop(),
      limit,
      subject_uri: uris,
    }
  },
  resolve: ([response, [option, resolve]]) => {
    const responseParsed = commentsResponseParser({ ...response, result: response.result[option.subject_uri] })
    resolve(responseParsed)
  },
})

/**
 * This is a mutualizer only used for comment stats as they all have a limit of 0
 */
const _getCommentsMutualizedStats = mutualizerDecorator<
typeof _getComments,
  GetCommentsParamsMutualized,
  ReturnType<typeof commentsResponseParser>
>({
  fetch: _getComments,
  merge: (bucketList) => {
    const uris = bucketList.map(el => el.subject_uri)
    return {
      ...bucketList.pop(),
      subject_uri: uris,
    }
  },
  resolve: ([response, [option, resolve]]) => {
    const responseParsed = commentsResponseParser({ ...response, result: response.result[option.subject_uri] })
    resolve(responseParsed)
  },
})

export function getComments(args: GetCommentsParamsMutualized) {
  // Set timeout 1 so the cache will stay alive until the first call answer.
  return cacheDecorator(`get-comments-mutualized-${args.subject_uri}`, () => _getCommentsMutualized(args), 1)
}

export function getCommentsStats(args: GetCommentsParamsMutualized) {
  // Set timeout 1 so the cache will stay alive until the first call answer.
  return cacheDecorator(`get-comments-stats-mutualized-${args.subject_uri}`, () => _getCommentsMutualizedStats({ ...args, limit: 0 }), 1)
}
/**
 * Get replies comments.
 * @param options - Options.
 * @param options.subject_uri - Subject URI.
 * @param options.comment_uri - Comment URI.
 * @param options.tenant - Platform id.
 * @param options.page - Page number to fetch.
 * @param options.offset - Offset number.
 * @param options.limit - Max number of comments to fetch.
 * @param options.sort - Sort by.
 * @param options.order - Order by.
 * @returns Promise with the fetched replies comments.
 */
export async function getReplies({
  subject_uri,
  comment_uri,
  tenant,
  page = 0,
  offset = 0,
  limit = 8,
  sort = 'time',
  order = 'reverse_time',
}: WithRequired<GetCommentsParams, 'subject_uri' | 'comment_uri'>):
  Promise<{ replies: IComment[], hasModerationRight: boolean }> {
  const platformId = tenant || getPlatformId()

  return swymFetch
    .get(`/commentproxy/subjects/${subject_uri}/comments/${comment_uri}`, {
      searchParams: {
        tenant: tenant || platformId,
        page_number: page,
        offset,
        page_size: limit,
        sort_by: sort,
        order_by: order,
      },
    })
    .then((response) => {
      return response
        .json<SwymResponse<CommentResponse[]>>()
        .then((result) => {
          return {
            hasModerationRight: response.headers.get('X-3dcomment-Has-Moderation-Rights') === 'true',
            replies: commentsParser(result.result),
          }
        })
    })
}

export async function getUsersWhoComment(options: GetCommentsParams & { subject_uri: string }) {
  return swymFetch
    .get(`/commentproxy/subjects/${options.subject_uri}/comments/users`, {
      searchParams: {
        tenant: options.tenant || getPlatformId(),
        page_number: options.page || 0,
        page_size: Number.isInteger(options.limit) ? (options.limit as number) : 8,
      },
    })
    .json<SwymResponse<SwymAuthor[]>>()
    .then((response) => {
      response.result = response.result.map(_userParser)
      return response as SwymResponse<IUser[]>
    })
}

export async function getUsersWhoCommentAReply(options: GetCommentsParams & { subject_uri: string }) {
  return swymFetch
    .get(`/commentproxy/subjects/${options.subject_uri}/comments/${options.comment_uri}/comment/users`, {
      searchParams: {
        tenant: options.tenant || getPlatformId(),
        page_number: options.page || 0,
        page_size: Number.isInteger(options.limit) ? (options.limit as number) : 8,
      },
    })
    .json<SwymResponse<SwymAuthor[]>>()
    .then((response) => {
      response.result = response.result.map(_userParser)
      return response as SwymResponse<IUser[]>
    })
}

export async function getUsersWhoEndorse(options: GetCommentsParams & { subject_uri: string }) {
  return swymFetch
    .get(`/commentproxy/subjects/${options.subject_uri}/endorses/users`, {
      searchParams: {
        tenant: options.tenant || getPlatformId(),
        page_number: options.page || 0,
        page_size: Number.isInteger(options.limit) ? (options.limit as number) : 8,
      },
    })
    .json<SwymResponse<SwymAuthor[]>>()
    .then((response) => {
      response.result = response.result.map(_userParser)
      return response as SwymResponse<IUser[]>
    })
}

export async function getUsersWhoEndorseAReply(options: GetCommentsParams & { subject_uri: string }) {
  return swymFetch
    .get(`/commentproxy/subjects/${options.subject_uri}/comments/${options.comment_uri}/endorses/users`, {
      searchParams: {
        tenant: options.tenant || getPlatformId(),
        page_number: options.page || 0,
        page_size: Number.isInteger(options.limit) ? (options.limit as number) : 8,
      },
    })
    .json<SwymResponse<SwymAuthor[]>>()
    .then((response) => {
      response.result = response.result.map(_userParser)
      return response as SwymResponse<IUser[]>
    })
}

// #region add/remove endorse
export async function addMyEndorse(options: GetCommentsParams & { subject_uri: string }) {
  return swymFetch
    .post(`/commentproxy/subjects/${options.subject_uri}/endorses/me`, {
      json: {},
      searchParams: { tenant: options.tenant || getPlatformId() },
    })
    .json<SwymResponse<UserEndorseResponse>>()
}

export async function removeMyEndorse(options: GetCommentsParams & { subject_uri: string }) {
  return swymFetch
    .delete(`/commentproxy/subjects/${options.subject_uri}/endorses/${options.endorse_uri}`, { searchParams: { tenant: options.tenant || getPlatformId() } })
    .json<SwymResponse<unknown>>()
}

export async function addMyEndorseToComment(options: GetCommentsParams & { subject_uri: string }) {
  return swymFetch
    .post(`/commentproxy/subjects/${options.subject_uri}/comments/${options.comment_uri}/endorses/me`, {
      json: {},
      searchParams: { tenant: options.tenant || getPlatformId() },
    })
    .json<SwymResponse<UserEndorseResponse>>()
}
export async function removeMyEndorseToComment(options: GetCommentsParams & { subject_uri: string }) {
  return swymFetch
    .delete(`/commentproxy/subjects/${options.subject_uri}/comments/${options.comment_uri}/endorses/${options.endorse_uri}`, { searchParams: { tenant: options.tenant || getPlatformId() } })
    .json()
}
// #endregion

export function createComment(
  richMessage: string,
  subject_uri: string,
  parent_uri?: string,
) {
  return swymFetch
    .post(`/commentproxy/subjects/${subject_uri}/comments`, {
      json: {
        parentCommentUri: parent_uri,
        richMessage,
      },
    })
    .json<SwymResponse<CommentResponse>>()
    .then((response) => {
      return { ...response, result: commentParser(response.result) } as SwymResponse<IComment>
    })
}

export function editComment(
  richMessage: string,
  subject_uri: string,
  message_uri: string,
  parent_uri?: string,
) {
  return swymFetch
    .put(`/commentproxy/subjects/${subject_uri}/comments/${message_uri}`, {
      json: {
        parentCommentUri: parent_uri,
        richMessage,
        commentUri: message_uri,
      },
    })
    .json<SwymResponse<CommentResponse>>()
    .then((response) => {
      return { ...response, result: commentParser(response.result) } as SwymResponse<IComment>
    })
}

export function deleteComment(subject_uri: string, comment_uri: string) {
  return swymFetch.delete(`/commentproxy/subjects/${subject_uri}/comments/${comment_uri}`)
}

export function getModerationRight(subject_uri: string) {
  return cacheDecorator(
    `moderation-right-${subject_uri}`,
    () => swymFetch.get(`/commentproxy/subjects/${subject_uri}/moderationrights`)
      .json<SwymResponse<boolean>>(),
    60000 * 5,
  )
} // 5mn cache

// #region fake response generators
export function getMockedCommentResponse(
  description: string,
  author: IUser,
  parent_comment_uri?: string,
): IComment {
  return {
    _acl: { update: { access: true }, delete: { access: true } },
    author,
    id: '',
    content_uri: '',
    subject_uri: '',
    nb_endorses: 0,
    nb_replies: 0,
    created_at: new Date()
      .toISOString(),
    modified_at: new Date()
      .toISOString(),
    access_state: 'active',
    moderation_state: ModerationStatus.NotModerated,
    parent_comment_uri,
    description,
    // question answer
    can_validate: false,
    is_validated: false,
    can_rate: false,
    has_rated: false,
    qna_id: '',
    moderated_at: new Date()
      .toISOString(),
    published_at: new Date()
      .toISOString(),
    updated_at: new Date()
      .toISOString(),
  } as IAnswer
}

export async function reportAbuse(options: {
  body: string
  reason: string
  subject_uri: string
  content_uri: string
  label: string
  type: string
}) {
  return swymFetch
    .post(`/api/v2/${options.type}/${options.subject_uri}/reportabuse`, {
      json: {
        reason: options.reason,
        body: options.body,
        content_uri: options.content_uri,
      },

    })
    .json()
}

/**
 * Fetches the comments corresponding to the uris contained in the given token.
 * @param token - JWT crafted by RTC server.
 * @returns The fetched comments.
 */
export function listCommentsUris(token: string, uris: string[]): Promise<IComment[]> {
  return commentFetch.extend({
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .post('/jwtComment/listCommentsUris', {
      json: uris,
    })
    .json<SwymResponse<CommentResponse[]>>()
    .then((response) => {
      const comments = commentsParser(response.result)
      return comments
    })
}

// #endregion
